// Fleet CP3 — vehicle detail. The modal stopped being enough once a vehicle
// carries maintenance schedules, service history, and an odometer log.
//   a) Maintenance schedules — recurring plans + add/edit (ScheduleModal)
//   b) Service history — records + "Log service" (RecordModal), which rolls a
//      linked schedule forward (date or mileage)
//   c) Odometer — current reading + inline update -> vehicle_odometer_log
// Equipment maintenance uses the SAME tables but has no detail page yet — it's
// CP-banked until there's demand.

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { ArrowLeft, Plus, Edit3, Gauge } from 'lucide-react'
import { toast } from 'sonner'
import {
  field, lbl, ModalShell, ExpiryBadge, StatusPill, VEHICLE_STATUS, VehicleModal, type VehicleRow,
} from './FleetPage'
import {
  scheduleDueState, formatInterval, type MaintenanceSchedule,
} from './maintenanceUtils'
import { DueBadge, ScheduleModal, RecordModal } from './MaintenanceModals'

interface RecordRow {
  id: string; performed_at: string; description: string; cost: number | null
  odometer_at_service: number | null; performed_by: string | null; schedule_id: string | null
}
interface OdoRow { id: string; reading: number; recorded_at: string; source: string }

const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }
const card: React.CSSProperties = { background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: 18 }

export default function VehicleDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [schedOpen, setSchedOpen] = useState(false)
  const [schedEditing, setSchedEditing] = useState<MaintenanceSchedule | null>(null)
  const [recOpen, setRecOpen] = useState(false)
  const [odoInput, setOdoInput] = useState('')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vehicle', id] })
    qc.invalidateQueries({ queryKey: ['vehicle-schedules', id] })
    qc.invalidateQueries({ queryKey: ['vehicle-records', id] })
    qc.invalidateQueries({ queryKey: ['vehicle-odo', id] })
    qc.invalidateQueries({ queryKey: ['vehicles', workspace?.id] })
    qc.invalidateQueries({ queryKey: ['fleet-vehicle-schedules', workspace?.id] })
  }

  const { data: vehicle, isLoading } = useQuery<VehicleRow | null>({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('vehicles').select('*').eq('id', id!).single()
      if (error) { console.error('[fleet] vehicle query failed', error); throw error }
      return data as VehicleRow
    },
    enabled: !!id,
  })
  const { data: schedules = [] } = useQuery<MaintenanceSchedule[]>({
    queryKey: ['vehicle-schedules', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_schedules').select('*').eq('vehicle_id', id!).order('created_at', { ascending: false })
      if (error) { console.error('[fleet] schedules query failed', error); throw error }
      return (data ?? []) as MaintenanceSchedule[]
    },
    enabled: !!id,
  })
  const { data: records = [] } = useQuery<RecordRow[]>({
    queryKey: ['vehicle-records', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_records').select('id, performed_at, description, cost, odometer_at_service, performed_by, schedule_id').eq('vehicle_id', id!).order('performed_at', { ascending: false })
      if (error) { console.error('[fleet] records query failed', error); throw error }
      return (data ?? []) as RecordRow[]
    },
    enabled: !!id,
  })
  const { data: odoLog = [] } = useQuery<OdoRow[]>({
    queryKey: ['vehicle-odo', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('vehicle_odometer_log').select('id, reading, recorded_at, source').eq('vehicle_id', id!).order('recorded_at', { ascending: false }).limit(12)
      if (error) { console.error('[fleet] odo query failed', error); throw error }
      return (data ?? []) as OdoRow[]
    },
    enabled: !!id,
  })
  const { data: techs = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['fleet-techs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id)
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  const updateOdo = useMutation({
    mutationFn: async () => {
      const reading = parseInt(odoInput, 10)
      if (!workspace?.id || !id || !Number.isFinite(reading)) throw new Error('Enter a reading')
      const { error } = await supabase.from('vehicle_odometer_log').insert({ workspace_id: workspace.id, vehicle_id: id, reading, source: 'manual' })
      if (error) throw error
    },
    onSuccess: () => { setOdoInput(''); invalidate() },
    onError: (e: any) => toast.error(e?.message || 'Failed to update reading'),
  })

  if (isLoading) return <div style={{ padding: 40, color: 'var(--ink-tertiary)' }}>Loading…</div>
  if (!vehicle) return <div style={{ padding: 40, color: 'var(--ink-tertiary)' }}>Vehicle not found. <Link to="/app/fleet" style={{ color: 'var(--accent-text)' }}>Back to Fleet</Link></div>

  const s = VEHICLE_STATUS[vehicle.status] ?? VEHICLE_STATUS.active
  const nowMs = Date.now()

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button onClick={() => navigate('/app/fleet')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--ink-tertiary)', fontSize: 13, cursor: 'pointer', padding: 0, width: 'fit-content' }}>
        <ArrowLeft style={{ width: 15, height: 15 }} /> Fleet
      </button>

      {/* Header */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: 'var(--ink-primary)' }}>{vehicle.name}</h1>
            <StatusPill meta={s} />
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '6px 0 0' }}>
            {[vehicle.make, vehicle.model, vehicle.year ? `(${vehicle.year})` : null].filter(Boolean).join(' ') || '—'}
            {vehicle.license_plate ? ` · ${vehicle.license_plate}` : ''}
          </p>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <ExpiryBadge date={vehicle.registration_expires_at} label="Reg" />
            <ExpiryBadge date={vehicle.insurance_expires_at} label="Ins" />
          </div>
        </div>
        <button onClick={() => setEditOpen(true)} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 'fit-content' }}>
          <Edit3 style={{ width: 14, height: 14 }} /> Edit
        </button>
      </div>

      {/* c) Odometer */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><Gauge style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} /><h2 style={sectionTitle}>Odometer</h2></div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div><span style={{ fontSize: 30, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>{vehicle.odometer != null ? vehicle.odometer.toLocaleString() : '—'}</span><span style={{ fontSize: 13, color: 'var(--ink-tertiary)', marginLeft: 6 }}>mi</span></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input value={odoInput} onChange={e => setOdoInput(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="New reading" style={{ ...field, width: 140 }} />
            <button onClick={() => updateOdo.mutate()} disabled={!odoInput || updateOdo.isPending} className="btn-primary">Update</button>
          </div>
        </div>
        {odoLog.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {odoLog.map(o => (
              <div key={o.id} style={{ fontSize: 12, color: 'var(--ink-tertiary)', display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--ink-secondary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{o.reading.toLocaleString()} mi</span>
                <span>{new Date(o.recorded_at).toLocaleDateString()}</span>
                {o.source === 'service' && <span style={{ color: 'var(--ink-tertiary)' }}>· from service</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* a) Maintenance schedules */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={sectionTitle}>Maintenance schedules</h2>
          <button onClick={() => { setSchedEditing(null); setSchedOpen(true) }} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}><Plus style={{ width: 13, height: 13 }} /> Add</button>
        </div>
        {schedules.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No schedules yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {schedules.map(sc => {
              const st = scheduleDueState(sc, vehicle.odometer, nowMs)
              const due = sc.interval_type === 'date' ? (sc.next_due_date ? `Next: ${sc.next_due_date}` : 'No next due') : (sc.next_due_miles != null ? `Next: ${sc.next_due_miles.toLocaleString()} mi` : 'No next due')
              return (
                <div key={sc.id} onClick={() => { setSchedEditing(sc); setSchedOpen(true) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-subtle)', cursor: 'pointer', opacity: sc.active ? 1 : 0.55 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{sc.title}{!sc.active && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 400 }}> · paused</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{formatInterval(sc)} · {due}</div>
                  </div>
                  <DueBadge state={st} />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* b) Service history */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={sectionTitle}>Service history</h2>
          <button onClick={() => setRecOpen(true)} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}><Plus style={{ width: 13, height: 13 }} /> Log service</button>
        </div>
        {records.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No service logged yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {records.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{r.description}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{r.performed_at}{r.odometer_at_service != null ? ` · ${r.odometer_at_service.toLocaleString()} mi` : ''}{r.performed_by ? ` · ${r.performed_by}` : ''}</div>
                </div>
                {r.cost != null && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-secondary)', whiteSpace: 'nowrap' }}>${r.cost.toFixed(2)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <VehicleModal open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} editing={vehicle} techs={techs} />
      <ScheduleModal open={schedOpen} onClose={() => setSchedOpen(false)} onSaved={() => { setSchedOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} target={{ vehicleId: id! }} editing={schedEditing} />
      <RecordModal open={recOpen} onClose={() => setRecOpen(false)} onSaved={() => { setRecOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} target={{ vehicleId: id! }} schedules={schedules} defaultOdometer={vehicle.odometer} />
    </div>
  )
}
